import apiClient from '@/lib/apiClient';
import { buildListParams, extractPaginatedData } from '@/shared/utils/queryUtils';

export type AssignmentMode = 'ASSIGN_TO_USER' | 'MATCH_OR_CREATE';

export type UserAssignment =
  | 'ASSIGNED_TO_OWNER'
  | 'MATCHED_BY_PHONE'
  | 'CREATED_FROM_PROFILE'
  | 'UNRESOLVED'
  | null;

export type ImportItem = {
  id: number;
  outcome: 'IMPORTED' | 'DUPLICATE' | 'FAILED';
  sourceId: string | null;
  sourceUrl: string | null;
  title: string | null;

  // Room-wanted target (new)
  createdRoomWantedAdId: number | null;
  existingRoomWantedAdId: number | null;

  // Legacy room-ad fields (older runs)
  createdAdId: number | null;
  existingAdId: number | null;

  assignedUserId: number | null;
  userAssignment: UserAssignment;
  normalizedPhone: string | null;

  imagesUploaded: number;
  message: string | null;
};

export type ImportRun = {
  runId: number;
  source: string;
  status: 'RUNNING' | 'SUCCESS' | 'PARTIAL' | 'FAILED';
  scanned: number;
  imported: number;
  skipped: number;
  failed: number;
  usersCreated: number;
  usersMatched: number;
  missingPhoneCount: number;
  unmappedValueCount: number;
  imageFailureCount: number;
  detailPagesFetched: number;
  recoveredPhoneCount: number;
  authFailureCount: number;
  summary: string | null;
  createdAt: string;
  finishedAt: string | null;
  triggeredByAdminEmail: string | null;
  items: ImportItem[];
};

export interface RunRoomatyBody {
  cityId: number;
  maxAds?: number;
  assignmentMode: AssignmentMode;
  ownerUserId?: number | null;
  status?: string;
  featured?: boolean;
  showPhone?: boolean;
  showWhatsApp?: boolean;
  showOwnerInfo?: boolean;
  showInAppMessaging?: boolean;
  hideLocation?: boolean;
}

export interface SupportedCity { id: number; nameEn: string; nameAr: string | null }
export interface SupportedCitiesResponse { cityIds: number[]; cities: SupportedCity[] }

export const importApi = {
  runRoomaty: async (body: RunRoomatyBody, images?: File[]): Promise<ImportRun> => {
    const formData = new FormData();
    formData.append('request', new Blob([JSON.stringify(body)], { type: 'application/json' }));
    if (images) images.forEach((f) => formData.append('images', f));
    const res = await apiClient.post('/admin/imports/roomaty', formData, {
      timeout: 240000,
      headers: { 'Content-Type': undefined },
    });
    return res.data?.data;
  },

  roomatySupportedCities: async (): Promise<SupportedCitiesResponse> => {
    const res = await apiClient.get('/admin/imports/roomaty/supported-cities');
    return res.data?.data ?? { cityIds: [], cities: [] };
  },

  listRuns: async (params: any) => {
    const queryParams = buildListParams(params);
    const res = await apiClient.get('/admin/imports/runs', { params: queryParams });
    return extractPaginatedData(res);
  },

  getRun: async (id: number): Promise<ImportRun> => {
    const res = await apiClient.get(`/admin/imports/runs/${id}`);
    return res.data?.data;
  },
};
